import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function StockKeeperAddEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        nickname: "",
        phone: "",
        password: "",
        password_confirmation: "",
        status: "1",
        opening_balance: 0,
        salary: 0,
        cnic: "",
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [pageLoading, setPageLoading] = useState(isEdit && !location.state?.stockKeeper);
    const [alert, setAlert] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
    });

    const populateForm = (item) => {
        const rawStatus = item.status;

        setFormData({
            name: item.name || "",
            email: item.email || "",
            nickname: item.nickname || "",
            phone: item.phone || "",
            password: "",
            password_confirmation: "",
            status: rawStatus === true || rawStatus === 1 || rawStatus === "1" ? "1" : "0",
            opening_balance: item.opening_balance ?? 0,
            salary: item.salary ?? 0,
            cnic: item.cnic || "",
        });
    };

    useEffect(() => {
        if (isEdit) {
            if (location.state?.stockKeeper) {
                populateForm(location.state.stockKeeper);
                setPageLoading(false);
            } else {
                const fetchStockKeeper = async () => {
                    try {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_URL}/admin/stock-keeper`,
                            { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
                        );
                        const stockKeepersList = Array.isArray(res.data) ? res.data : (res.data.data || []);
                        const item = stockKeepersList.find((sk) => sk.id === parseInt(id));

                        if (item) {
                            populateForm(item);
                        } else {
                            setAlert({ type: "danger", message: "Stock keeper not found." });
                        }
                    } catch (err) {
                        setAlert({ type: "danger", message: "Failed to load stock keeper details." });
                    } finally {
                        setPageLoading(false);
                    }
                };
                fetchStockKeeper();
            }
        }
    }, [id, isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        const payload = { ...formData };
        if (isEdit && !payload.password) {
            delete payload.password;
            delete payload.password_confirmation;
        }

        let url = `${process.env.REACT_APP_API_URL}/admin/stock-keeper`;
        if (isEdit) {
            url = `${process.env.REACT_APP_API_URL}/admin/stock-keeper/${id}`;
        }

        try {
            const response = await axios.post(url, payload, getAuthHeader());

            if (response.status === 200 || response.status === 201 || response.data?.success) {
                navigate("/admin/stock-keeper");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (pageLoading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("Loading stock keeper data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4 p-4 rounded-4 shadow-sm border-0 bg-white d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h3 className="mc-breadcrumb-title fw-bold text-dark m-0">
                                {isEdit ? t("Edit Stock Keeper") : t("Add Stock Keeper")}
                            </h3>
                            <ul className="mc-breadcrumb-list mt-1 m-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link text-secondary">{t("Home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/admin/stock-keeper" className="mc-breadcrumb-link text-secondary">{t("Stock Keepers")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item text-primary fw-semibold">{isEdit ? t("Edit") : t("Add")}</li>
                            </ul>
                        </div>
                        <Button variant="secondary" className="rounded-3 px-4" onClick={() => navigate("/admin/stock-keeper")}>
                            {t("Back")}
                        </Button>
                    </div>
                </Col>

                <Col xl={12} className="mx-auto">
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="rounded-3 shadow-sm">
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Name <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                placeholder="e.g. John Doe"
                                                value={formData.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.name?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Nickname <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nickname"
                                                placeholder="e.g. Johnny"
                                                value={formData.nickname}
                                                onChange={handleChange}
                                                isInvalid={!!errors.nickname}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.nickname?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Email <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="e.g. stockkeeper@example.com"
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.email?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Phone <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                placeholder="e.g. 03001234567"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                isInvalid={!!errors.phone}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.phone?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Opening Balance</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="opening_balance"
                                                placeholder="0"
                                                value={formData.opening_balance}
                                                onChange={handleChange}
                                                isInvalid={!!errors.opening_balance}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.opening_balance?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Salary</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="salary"
                                                placeholder="0"
                                                value={formData.salary}
                                                onChange={handleChange}
                                                isInvalid={!!errors.salary}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.salary?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">CNIC</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="cnic"
                                                placeholder="e.g. 35201-1234567-1"
                                                value={formData.cnic}
                                                onChange={handleChange}
                                                isInvalid={!!errors.cnic}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.cnic?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Status <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                                isInvalid={!!errors.status}
                                                required
                                            >
                                                <option value="1">Active</option>
                                                <option value="0">Inactive</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.status?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {isEdit ? "New Password (Optional)" : <>Password <span className="text-danger">*</span></>}
                                            </Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    placeholder="Enter password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.password}
                                                    required={!isEdit}
                                                    className="pe-5"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="position-absolute top-50 translate-middle-y end-0 me-2 p-0 text-secondary text-decoration-none"
                                                    style={{ transform: "translateY(-50%)" }}
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                >
                                                    {showPassword ? "Hide" : "Show"}
                                                </Button>
                                            </div>
                                            <Form.Control.Feedback type="invalid">{errors.password?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {(formData.password || !isEdit) && (
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">
                                                    Confirm Password <span className="text-danger">*</span>
                                                </Form.Label>
                                                <div className="position-relative">
                                                    <Form.Control
                                                        type={showConfirmPassword ? "text" : "password"}
                                                        name="password_confirmation"
                                                        placeholder="Re-enter password"
                                                        value={formData.password_confirmation}
                                                        onChange={handleChange}
                                                        required={!isEdit || Boolean(formData.password)}
                                                        className="pe-5"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="position-absolute top-50 translate-middle-y end-0 me-2 p-0 text-secondary text-decoration-none"
                                                        style={{ transform: "translateY(-50%)" }}
                                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                                    >
                                                        {showConfirmPassword ? "Hide" : "Show"}
                                                    </Button>
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    )}
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4 pt-3 border-top">
                                    <Button variant="secondary" className="px-4 border" onClick={() => navigate("/admin/stock-keeper")}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="submit" className="px-4" disabled={submitting}>
                                        {submitting ? "Saving..." : isEdit ? "Update Stock Keeper" : "Save Stock Keeper"}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}